import React, { useCallback, useMemo } from 'react'

import { Table, TableRow } from '../../../../../components/pdf/Table'
import { TableColText, TableHeadText } from '../../../../../components/pdf/TableHelpers'
import Separator from '../../../../../components/pdf/Separator'
import Text from '../../../../../components/pdf/Text'
import View from '../../../../../components/pdf/View'
import {
  filterRepliesByQuestionName,
  getMeasurementResult,
  findParentReply,
  getJobDescription
} from '../../_helpers/pdfHelpers'

const EnvironmentalConditionSection = ({ serviceOrder }) => {
  const max = useCallback(
    (setor, pergunta) => {
      let maior = 0
      const ruidos = filterRepliesByQuestionName(serviceOrder.replies, pergunta)
      ruidos.forEach(ruido => {
        const parent = findParentReply(serviceOrder.replies, ruido)
        if (parent.question.name.includes(setor) && ruido.value > maior) {
          maior = ruido.value
        }
      })

      return maior
    },
    [serviceOrder.replies]
  )

  const remapIluminamento = () => {
    const respostasDeIluminamento = filterRepliesByQuestionName(serviceOrder.replies, 'ilum. (lux) - nho 11 [1]')
    const resultado = {
      Atendimento: [],
      Comercial: []
    }
    respostasDeIluminamento.forEach(resposta => {
      const parent = findParentReply(serviceOrder.replies, resposta)
      const { setor, funcao } = getJobDescription(parent.question.name)
      if (!resultado[setor]) {
        resultado[setor] = []
      }
      resultado[setor].push({ setor, funcao, valor: resposta.value })
    })

    return resultado
  }
  const iluminamentos = remapIluminamento()

  const pegarMenor = useCallback(
    (setor, pergunta) => {
      let menor = 999999
      const ruidos = filterRepliesByQuestionName(serviceOrder.replies, pergunta)
      ruidos.forEach(ruido => {
        const parent = findParentReply(serviceOrder.replies, ruido)
        if (parent.question.name.includes(setor) && ruido.value < menor) {
          menor = ruido.value
        }
      })

      return menor
    },
    [serviceOrder.replies]
  )

  const temRespostaDeAtendimento = useMemo(() => {
    const replies = filterRepliesByQuestionName(serviceOrder.replies, 'Área de Atendimento')
    const replyFound = replies.find(reply => reply.value)
    return replyFound
  }, [serviceOrder.replies])

  const temRespostaDeComercial = useMemo(() => {
    const replies = filterRepliesByQuestionName(serviceOrder.replies, 'Área Comercial')
    const replyFound = replies.find(reply => reply.value)
    return replyFound
  }, [serviceOrder.replies])

  const ruidos = useMemo(() => {
    const resultado = []

    if (temRespostaDeAtendimento && temRespostaDeComercial) {
      resultado[0] = { setor: 'GHE 1 – Atendimento', valor: max('Atendimento', 'RUIDO - DB(A) [1]') }
      resultado[1] = { setor: 'GHE 2 – Comercial', valor: max('Comercial', 'RUIDO - DB(A) [1]') }
    }
    if (temRespostaDeAtendimento) {
      resultado[0] = { setor: 'GHE 1 – Atendimento', valor: max('Atendimento', 'RUIDO - DB(A) [1]') }
    } else {
      resultado[0] = { setor: 'GHE 1 – Comercial', valor: max('Comercial', 'RUIDO - DB(A) [1]') }
    }
    return resultado
  }, [max, temRespostaDeComercial, temRespostaDeAtendimento])

  const temperaturas = useMemo(() => {
    const resultado = []

    if (temRespostaDeAtendimento && temRespostaDeComercial) {
      resultado[0] = { setor: 'GHE 1 – Atendimento', valor: max('Atendimento', 'temp. (tbs) - ºc [1]') }
      resultado[1] = { setor: 'GHE 2 – Comercial', valor: max('Comercial', 'temp. (tbs) - ºc [1]') }
    }
    if (temRespostaDeAtendimento) {
      resultado[0] = { setor: 'GHE 1 – Atendimento', valor: max('Atendimento', 'temp. (tbs) - ºc [1]') }
    } else {
      resultado[0] = { setor: 'GHE 1 – Comercial', valor: max('Comercial', 'temp. (tbs) - ºc [1]') }
    }
    return resultado
  }, [max, temRespostaDeComercial, temRespostaDeAtendimento])

  const umidades = useMemo(() => {
    const resultado = []

    if (temRespostaDeAtendimento && temRespostaDeComercial) {
      resultado[0] = { setor: 'GHE 1 – Atendimento', valor: pegarMenor('Atendimento', 'u.r (%) [1]') }
      resultado[1] = { setor: 'GHE 2 – Comercial', valor: pegarMenor('Comercial', 'u.r (%) [1]') }
    }
    if (temRespostaDeAtendimento) {
      resultado[0] = { setor: 'GHE 1 – Atendimento', valor: pegarMenor('Atendimento', 'u.r (%) [1]') }
    } else {
      resultado[0] = { setor: 'GHE 1 – Comercial', valor: pegarMenor('Comercial', 'u.r (%) [1]') }
    }
    return resultado
  }, [pegarMenor, temRespostaDeComercial, temRespostaDeAtendimento])

  return (
    <View noWrap id="9">
      <Text bold size="small">
        9. CONDIÇÕES AMBIENTAIS DE TRABALHO
      </Text>
      <Separator weight={8} />
      <Text bold size="small">
        9.1. AVALIAÇÃO DE CONFORTO ACÚSTICO
      </Text>
      <Separator weight={8} />
      <Text bold>Quadro De Avaliação De Ruído</Text>
      <Text>
        Segundo a NR-17 (Ergonomia) os locais de trabalho onde são executadas atividades que exijam solicitação
        intelectual e atenção constantes, tais como: salas de controle, laboratórios, escritórios, salas de
        desenvolvimento ou análise de projetos, dentre outros, é recomendado que os níveis de ruído devem atender o
        estabelecido na NBR10.152:1992 (ABNT - Associação Brasileira de Normas Técnicas);
      </Text>
      <Text>
        Para as atividades que possuam as características definidas na NBR 10.152:1992, mas não apresentam equivalência
        ou correlação com aquelas relacionadas, o nível de ruído aceitável para efeito de conforto será de até 65 dB (A)
        e a curva de avaliação de ruído (NC) de valor não superior a 60 dB.
      </Text>
      <Separator weight={8} />
      <Table width="80%">
        <TableRow>
          <TableHeadText width="140px">Setor</TableHeadText>
          <TableHeadText width="90px">Ruído dB (A) Encontrado</TableHeadText>
          <TableHeadText>Observações</TableHeadText>
          <TableHeadText>Valores de referência NBR 10152/92</TableHeadText>
          <TableHeadText width="140px">Conclusão</TableHeadText>
        </TableRow>
        {ruidos.map((ruido, index) => (
          <TableRow key={index}>
            <TableColText>{ruido.setor}</TableColText>
            <TableColText>{ruido.valor}</TableColText>
            <TableColText>{getMeasurementResult('ruido', ruido.valor)}</TableColText>
            <TableColText>65dB(A)</TableColText>
            <TableColText>Ruído Ambiente</TableColText>
          </TableRow>
        ))}
      </Table>
      <Separator weight={8} />
      <Text bold size="small">
        9.2. AVALIAÇÃO DE NÍVEIS DE TEMPERATURA, UMIDADE E VELOCIDADE DO AR
      </Text>
      <Separator weight={8} />
      <Text>
        As medições foram realizadas nos setores, posicionando os equipamentos no plano de trabalho onde se realiza a
        tarefa, comparando com valores estabelecidos pela NR – 17;
      </Text>
      <Separator weight={8} />
      <Table width="75%">
        <TableRow>
          <TableHeadText width="140px">Setor</TableHeadText>
          <TableHeadText>Temperatura Efetiva (ºC) 18-25ºC</TableHeadText>
          <TableHeadText>Conclusão</TableHeadText>
        </TableRow>
        {temperaturas.map((temperatura, index) => (
          <TableRow key={index}>
            <TableColText>{temperatura.setor}</TableColText>
            <TableColText>{temperatura.valor}</TableColText>
            <TableColText>Temperatura Efetiva {getMeasurementResult('temperatura', temperatura.valor)}</TableColText>
          </TableRow>
        ))}
      </Table>
      <Separator weight={8} />
      <Table width="75%">
        <TableRow>
          <TableHeadText width="140px">Setor</TableHeadText>
          <TableHeadText width="120px">Umidade Relativa do ar (%)</TableHeadText>
          <TableHeadText width="80px">Velocidade do ar (m/s²)</TableHeadText>
          <TableHeadText>Conclusão</TableHeadText>
        </TableRow>
        {umidades.map((umidade, index) => (
          <TableRow key={index}>
            <TableColText>{umidade.setor}</TableColText>
            <TableColText>{umidade.valor}</TableColText>
            <TableColText>0,01</TableColText>
            <TableColText>Umidade Relativa {getMeasurementResult('umidade', umidade.valor)}</TableColText>
          </TableRow>
        ))}
      </Table>
      <Separator weight={8} />
      <Text bold size="small">
        9.3. AVALIAÇÃO DE NÍVEIS DE ILUMINAMENTO
      </Text>
      <Separator weight={8} />
      <Text>
        As medições foram realizadas nos setores que possuem iluminação natural e artificial, posicionando a fotocélula
        no plano de trabalho onde se realiza a tarefa, comparando e utilizando como parâmetro técnico os valores
        estabelecidos na NHO 11 (Norma de Higiene Ocupacional) item 22 Quadro I “Escrever, teclar, ler e processar
        dados: 500 lux”. Entretanto, conforme reza o item 5 da legislação mencionada é permitido uma tolerância de 10%
        abaixo desse valor, portanto o nível mínimo de lux aceitável é de 450 lux. conforme Norma Regulamentadora NR-17
        item 17.5.3.3, aprovada pela Portaria nº3.214/78 do Ministério do Trabalho e Emprego – STRAB. A utilização de
        uma iluminação adequada proporciona um ambiente de trabalho agradável, melhorando as condições e diminuindo as
        possibilidades de acidente. Em todos os locais de trabalho deve haver iluminação adequada, natural ou
        artificial, geral ou suplementar, apropriada à natureza da atividade.
      </Text>
      <Separator weight={8} />
      <Table width="100%">
        <TableRow>
          <TableHeadText>Setor</TableHeadText>
          <TableHeadText>Função</TableHeadText>
          <TableHeadText>Iluminamento (Lux)</TableHeadText>
          <TableHeadText>Conclusão/Recomendação</TableHeadText>
        </TableRow>
        {temRespostaDeAtendimento && (
          <>
            <TableRow>
              <TableColText bold rowSpan={iluminamentos['Atendimento'].length + 1}>
                Atendimento
              </TableColText>
            </TableRow>
            {iluminamentos['Atendimento'].map((resposta, index) => {
              return (
                <TableRow key={index}>
                  <TableColText>{resposta.funcao}</TableColText>
                  <TableColText>{resposta.valor}</TableColText>
                  <TableColText>{getMeasurementResult('iluminamento', resposta.valor)}</TableColText>
                </TableRow>
              )
            })}
          </>
        )}

        {temRespostaDeComercial && (
          <TableRow>
            <TableColText bold rowSpan={iluminamentos['Comercial'].length + 1}>
              Comercial
            </TableColText>
          </TableRow>
        )}
        {iluminamentos['Comercial'].map((resposta, index) => {
          return (
            <TableRow key={index}>
              <TableColText>{resposta.funcao}</TableColText>
              <TableColText>{resposta.valor}</TableColText>
              <TableColText>{getMeasurementResult('iluminamento', resposta.valor)}</TableColText>
            </TableRow>
          )
        })}
      </Table>
      <Separator weight={8} />
    </View>
  )
}
export default EnvironmentalConditionSection
